import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../utils/supabaseClient';

const NewsPreview = () => {
  const [expandedNews, setExpandedNews] = useState(null);

  // remove mock data; rely only on data from the database
  const mockNews = [];

  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchLatestNews() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('news')
          .select('id, title, content, category, image_url, media_urls, publish_date, created_at')
          .order('publish_date', { ascending: false })
          .limit(6);
        if (error) throw error;
        const mapped = (data || []).map((n) => {
          // แปลง media_urls จาก JSON string เป็น array
          let mediaUrls = [];
          if (n.media_urls) {
            try {
              mediaUrls = typeof n.media_urls === 'string' 
                ? JSON.parse(n.media_urls) 
                : (Array.isArray(n.media_urls) ? n.media_urls : []);
            } catch (e) {
              mediaUrls = [];
            }
          }
          
          return {
            id: n.id,
            title: n.title,
            summary: (n.content || '').slice(0, 160),
            content: n.content || '',
            imageUrl: n.image_url || '',
            mediaUrls: mediaUrls,
            date: n.publish_date || n.created_at,
            category: n.category || 'ข่าวสาร',
            priority: 'ปกติ',
            author: ''
          };
        });
        if (isMounted) setNewsItems(mapped);
      } catch (e) {
        // no mock fallback
        if (isMounted) setNewsItems([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchLatestNews();
    return () => {
      isMounted = false;
    };
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'สูง':
        return 'bg-error text-error-foreground';
      case 'ปกติ':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const toggleExpand = (newsId) => {
    setExpandedNews(expandedNews === newsId ? null : newsId);
  };

  return (
    <section className="py-12 lg:py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
            ข่าวประชาสัมพันธ์
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            ข่าวสารและประกาศสำคัญจากที่ว่าการอำเภอเทิง
          </p>
        </div>

        {/* News horizontal marquee using existing Tailwind animate-marquee effect */}
        <div className="relative flex w-full flex-col items-center justify-center overflow-hidden mb-8">
          <div className="group flex overflow-hidden p-2 [--gap:1rem] [gap:var(--gap)] flex-row [--duration:40s]">
            <div className="flex shrink-0 justify-around [gap:var(--gap)] animate-marquee flex-row group-hover:[animation-play-state:paused]">
              {[...Array(2)].map((_, setIndex) => (
                newsItems.map((news, idx) => {
                  // หารูปภาพแรกที่จะแสดง (จาก media_urls หรือ image_url)
                  let displayImage = null;
                  if (news?.mediaUrls && news.mediaUrls.length > 0) {
                    const firstImage = news.mediaUrls.find(m => m.type === 'image');
                    if (firstImage) {
                      displayImage = firstImage.url;
                    }
                  }
                  if (!displayImage && news?.imageUrl) {
                    displayImage = news.imageUrl;
                  }
                  
                  return (
                    <div
                      key={`${setIndex}-${news?.id}-${idx}`}
                      className="bg-card rounded-lg shadow-government hover:shadow-government-lg transition-government overflow-hidden w-80 flex-shrink-0 flex flex-col"
                    >
                      {/* แสดงรูปภาพด้านบน */}
                      {displayImage && (
                        <div className="w-full h-48 overflow-hidden bg-muted">
                          <img
                            src={displayImage}
                            alt={news?.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                      )}
                      
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-start justify-between mb-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(news?.priority)}`}>
                            {news?.priority}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(news?.date)}
                          </span>
                        </div>

                        <h3 className="text-lg font-semibold text-foreground mb-3 leading-tight line-clamp-2">
                          {news?.title}
                        </h3>

                        <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-3 flex-1">
                          {expandedNews === news?.id ? news?.content : news?.summary}
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-border mt-auto">
                          <div className="flex items-center space-x-2">
                            <Icon name="Tag" size={14} className="text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{news?.category}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="ChevronRight"
                            iconPosition="right"
                            asChild
                          >
                            <Link to={`/news/${news?.id}`}>
                              อ่านต่อ
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ))}
            </div>
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 hidden w-1/3 bg-gradient-to-r from-muted/30 sm:block" />
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/3 bg-gradient-to-l from-muted/30 sm:block" />
        </div>

        <div className="text-center">
          <Button
            variant="outline"
            iconName="Newspaper"
            iconPosition="left"
            asChild
          >
            <Link to="/news-and-announcements">
              ดูข่าวทั้งหมด
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default NewsPreview;